import { useCallback } from 'react';

import { AttachmentItemData } from '90.quickConnect.Models/models';
import { FieldType } from '90.quickConnect.Models/enums';
import attachmentDb from '40.quickConnect.DataAccess/indexedDb/dbs/attachmentDb';
import CustomLogger from '80.quickConnect.Core/logger/customLogger';

const useData = (attachment: AttachmentItemData, fieldType: FieldType, onlyImage: boolean) => {
  const logger = CustomLogger.getInstance();
  const { file, type, fileName, thumbnail, distantUri, id } = attachment;

  const createFileFromBase64 = useCallback((): File => {
    const base64Str = thumbnail;
    const bstr = window.atob(base64Str);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], fileName, { type });
  }, [thumbnail, fileName, type]);

  // Résolue au clic uniquement, pour éviter une lecture IndexedDB et une URL blob par item à chaque rendu
  const resolveImageSrc = useCallback(async (): Promise<string | undefined> => {
    if (!onlyImage) return undefined;
    if (file instanceof File) return URL.createObjectURL(file);
    if (distantUri?.startsWith('https')) return distantUri;
    if (!thumbnail || !fileName || !type) return undefined;

    if (id) {
      const attachItemDb = await attachmentDb.getAttachmentById(id);
      return attachItemDb?.file ? URL.createObjectURL(attachItemDb.file) : undefined;
    }

    return URL.createObjectURL(createFileFromBase64());
  }, [onlyImage, file, distantUri, thumbnail, fileName, type, id, createFileFromBase64]);

  // N'ouvre pas d'onglet vide si aucune source n'est disponible (ni fichier, ni distantUri, ni blob local)
  const openLocalImage = useCallback(async () => {
    try {
      const src = await resolveImageSrc();
      if (!src) return;

      window.open(src, '_blank', 'noopener');
    } catch (error) {
      logger.warn('OpenLocalImage', `'openLocalImage' failed for attachment ${id}: ${error}`);
    }
  }, [resolveImageSrc, logger, id]);

  return { openLocalImage, distantUri };
};

export default useData;
